import { useEffect, useRef } from 'react';
import { colors, fonts, layout, spacing } from '../theme';
import Icon from './Icon';

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000,
    padding: spacing.md,
    animation: 'fadeIn 0.2s ease',
  },
  modal: {
    backgroundColor: colors.white,
    borderRadius: layout.borderRadius,
    boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.2)',
    maxWidth: '600px',
    width: '100%',
    maxHeight: '90vh',
    overflow: 'auto',
    animation: 'slideUp 0.3s ease',
    position: 'relative',
  },
  header: {
    padding: spacing.lg,
    borderBottom: `1px solid ${colors.gray200}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    fontFamily: fonts.heading,
    fontSize: '1.5rem',
    fontWeight: 600,
    color: colors.darkGray,
    margin: 0,
  },
  closeButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: spacing.xs,
    display: 'flex',
    alignItems: 'center',
    color: colors.midGray,
    transition: 'color 0.2s ease',
  },
  body: {
    padding: spacing.lg,
  },
  footer: {
    padding: spacing.lg,
    borderTop: `1px solid ${colors.gray200}`,
    display: 'flex',
    justifyContent: 'flex-end',
    gap: spacing.md,
  },
};

function Modal({
  isOpen,
  onClose,
  title,
  children,
  footer = null,
  showCloseButton = true,
  closeOnOverlayClick = true,
  size = 'medium',
  ...props
}) {
  const modalRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      // Focus trap
      const firstFocusable = modalRef.current?.querySelector(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (firstFocusable) {
        firstFocusable.focus();
      }
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const sizeStyles = {
    small: { maxWidth: '400px' },
    medium: { maxWidth: '600px' },
    large: { maxWidth: '800px' },
    full: { maxWidth: '95vw', maxHeight: '95vh' },
  };

  const modalStyle = {
    ...styles.modal,
    ...sizeStyles[size],
  };

  const handleOverlayClick = (e) => {
    if (closeOnOverlayClick && e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      style={styles.overlay}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'modal-title' : undefined}
    >
      <div ref={modalRef} style={modalStyle} {...props}>
        {(title || showCloseButton) && (
          <div style={styles.header}>
            {title && <h2 id="modal-title" style={styles.title}>{title}</h2>}
            {showCloseButton && (
              <button
                style={styles.closeButton}
                onClick={onClose}
                aria-label="Close modal"
                onMouseEnter={(e) => (e.target.style.color = colors.darkGray)}
                onMouseLeave={(e) => (e.target.style.color = colors.midGray)}
              >
                <Icon name="close" type="status" size={24} />
              </button>
            )}
          </div>
        )}
        <div style={styles.body}>{children}</div>
        {footer && <div style={styles.footer}>{footer}</div>}
      </div>
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @media (prefers-reduced-motion: reduce) {
          * {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }
      `}</style>
    </div>
  );
}

export default Modal;

